import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useAtomValue, useSetAtom } from 'jotai'

import * as lib from '../../lib'
import * as state from '../../states'
import { IRole, ETableRemoveType } from '../../types'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IRoleRemoveModalBase = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

type IRoleRemoveModalProps = IRoleRemoveModalBase & {
	type: ETableRemoveType.single;
	item: IRole | null;
}

type IRolesRemoveModalProps = IRoleRemoveModalBase & {
	type: ETableRemoveType.multi;
	items: IRole[] | null;
}

export const ItemBox = styled.div`
    padding-top: 2px;
`

export const IdBox = styled.span`
    display: inline-block;
    min-width: 85px;
`

export const NotSystemItems = styled.div`
    margin-bottom: 50px;
`

export const SystemItems = styled.div`
    margin-bottom: 50px;
    opacity: 0.5;
`

export const ModalBodyBox = styled.h3`
    word-break: break-all;
    white-space: normal;
`

export const RoleRemoveModal = (
	props: IRoleRemoveModalProps | IRolesRemoveModalProps
) => {
	const { t } = useTranslation()
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)

	const { type, show, onClose, onSave } = props

	const isTypeSingle = type === ETableRemoveType.single
	const item = type === ETableRemoveType.single ? props.item : null
	const items = type === ETableRemoveType.multi ? props.items : null
	const notSystemItems = items?.filter(item => !item.isSystem) ?? null
	const systemItems = items?.filter(item => !!item.isSystem) ?? null
	const notSystemIds = notSystemItems?.map(item => item?.id ?? 0) ?? []

	const isRemoveBtnActive = type === ETableRemoveType.single ?
		item && !item.isSystem :
		notSystemItems && notSystemItems.length > 0

	if (!show) {
		return null
	}
	//console.log('RoleRemoveModal: ', { items, notSystemItems, systemItems, isRemoveBtnActive })
	const onRemove = async () => {
		const uri: string = isTypeSingle ? `role/${item?.id}` : `role?ids=${notSystemIds}`
		const method: string = 'DELETE'

		// request for multi delete: role?ids=${[1,2,3,4]}
		// response: {items:[{id: 1, result: true}, ....]}

		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)
		const options: lib.FetchOptions = { method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			const response = await lib.fetching<void>(uri, options)
			onSave()
			//TODO: check response for each item (true/false) ...?!
			//console.log('onRemove: ', {response})
			if ((response as any)?.result) {
				const resObj = (response as any)?.result ?? {}
				const resEntries = Object.entries(resObj)
				const isSomeError = resEntries?.some(([key, value]) => !value)

				if (isSomeError) {
					throw resObj
				}
			}
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const renderSingleItem = () => {
		const itemLabel = item?.name ?? ''

		return (
			<NotSystemItems>
				<Trans
					i18nKey={ALL_LANG_KEY.role_crud_modal.title_remove_single}
					components={{ span: <span className={'text-danger'}/> }}
				/>:
				<br/>
				<br/>
				<ItemBox key={`role-${item?.id}`}
					// className={'text-danger'}
				>
					- <IdBox className={'text-danger'}>[id: {item?.id}]</IdBox> {itemLabel}
				</ItemBox>
			</NotSystemItems>
		)
	}

	const renderNotSystemItems = () => {
		if (!notSystemItems?.length) return null

		return (
			<NotSystemItems>
				<Trans
					i18nKey={ALL_LANG_KEY.role_crud_modal.title_remove_multiple_cnt}
					components={{
						span: <span className={'text-danger'}/>,
						cnt: notSystemItems?.length ?? ''
					}}
				/>:
				<br/>
				<br/>
				{notSystemItems?.map((item, index) => {
					const itemLabel = item?.name ?? ''

					return (
						<ItemBox key={`role-${item?.id ?? index}`}
							// className={'text-danger'}
						>
							- <IdBox className={'text-danger'}>[id: {item?.id}]</IdBox> {itemLabel}
						</ItemBox>
					)
				})}
			</NotSystemItems>
		)
	}

	const renderSystemItems = () => {
		if (!systemItems?.length) return null

		return (
			<SystemItems>
				<Trans
					i18nKey={ALL_LANG_KEY.role_crud_modal.title_not_remove_multiple_cnt}
					components={{
						span: <span className={'text-danger'}/>,
						cnt: systemItems?.length ?? ''
					}}
				/>:
				<br/>
				<br/>
				{systemItems?.map((item, index) => {
					const itemLabel = item?.name ?? ''

					return (
						<ItemBox key={`role-${item?.id ?? index}`}
							// className={'text-danger'}
						>
							- <IdBox className={'text-danger'}>[id: {item?.id}]</IdBox> {itemLabel}
						</ItemBox>
					)
				})}
			</SystemItems>
		)
	}

	const modalTitle = (
		<Trans
			i18nKey={isTypeSingle ? ALL_LANG_KEY.role_crud_modal.title_remove_single : ALL_LANG_KEY.role_crud_modal.title_remove_multiple}
			components={{ span: <></> }}
		/>
	)
	const modalBody = isTypeSingle ? (
		<>
			{renderSingleItem()}
		</>
	) : (
		<>
			{renderNotSystemItems()}
			{renderSystemItems()}
		</>
	)
	const msgRemoveConfirm = `${t(ALL_LANG_KEY.role_crud_modal.msg_remove_confirm)}`

	return <>
		<Modal show={show}
		       onHide={onClose}
		       scrollable={true}
		       size={"lg"}
		>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className={'scroll'}>
				<ModalBodyBox className={'fs-25'}>
					{modalBody}
					<span className={'text-primary'}>{msgRemoveConfirm}</span>
				</ModalBodyBox>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary"
				        onClick={onRemove}
				        disabled={!isRemoveBtnActive}
				>
					{`${t(ALL_LANG_KEY.role_crud_modal.btn_remove)}`}
				</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.role_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
